/**
 * Fold a chunk's voxels onto its parent's coarser lattice — the dense sibling of `foldVoxels`.
 *
 * `foldVoxels` folds a MODEL (a sparse `VoxelSample[]` authored around an origin); this folds a
 * REGION OF A CHUNK into the matching octant of its parent chunk, which is what an edit cascade
 * needs. The reduction rule is the same one, for the same reasons — see fold.ts: STRONGEST WEIGHT
 * WINS THE CELL, so a cell mixing wall and carve goes solid (openings shrink by up to one coarse
 * voxel per edge, walls never erode), and a tie goes to the emissive material.
 *
 * A 2x2x2 group NEVER STRADDLES A CHUNK BOUNDARY: a lod-n chunk is 32 voxels across and spans
 * exactly 16 of its parent's, so a parent voxel's eight children all live in ONE child chunk. That
 * is what makes an edit cascade local — a level only ever needs the one child chunk it came from,
 * never the other seven siblings.
 *
 * KNOWN vs UNKNOWN. A fold writes a parent voxel only when all eight children are KNOWN. A layer
 * built this way carries the invariant "a parent entry exists ⟹ all eight of its children have
 * entries", so a later fold over the same box can never half-inform a voxel (which would drop a
 * previous edit's contribution and read as a hole). At lod 0 the child is a real chunk array —
 * everything in it is known — so `childKnown` is null there.
 *
 * WEIGHT IS RE-MEASURED ON THE COARSE LATTICE, which is the same lesson `generateChunkLOD` and
 * `rasterizePartsAtScale` both had to learn: `sdfToWeight` ramps over METRES, so a thin authored
 * object carries tiny fine weights, and copying them onto a lattice whose voxels are 2^lod times
 * wider leaves the surface in a near-zero crossing band. Measured on the Torch preset (a 1.4-voxel
 * column plus a radius-1 lava tip): its ten solid fine voxels pack at 0.03-0.30 against a full 0.50,
 * and folding them verbatim put the lod-1 crossing 6% of a cell away from the voxel centre — a
 * degenerate, effectively invisible mesh. So the FIRST fold (from a real chunk array) re-measures a
 * solid winner as FULLY solid; air keeps its max, so carve interpolation and the water crossing are
 * untouched.
 *
 * ...AND THEN DECAYS ONE STEP PER LEVEL WHILE THE CELL IS ONLY PARTLY FILLED. Iterated max-pooling
 * of a sub-voxel feature is a blob generator: saturate at every level and a 0.5 m torch reads as a
 * solid 8 m cube by lod 5. Averaging instead is the opposite failure — it dissolves the 2-voxel walls
 * this whole rule exists to protect. So a FULL cell (all eight children solid) keeps its weight
 * forever, which is what makes a real mass stay solid at every level, and a PARTIAL cell loses one
 * packed step per level, floored one step above air. A thin feature therefore never disappears and
 * never inflates: it fades — 0.500 at lod 1, 0.433, 0.367, 0.300, 0.233 at lod 5, down to 0.033.
 *
 * PARTIAL KNOWLEDGE IS ALLOWED, and that is what lets a build climb to the TOP of the hierarchy. A
 * cell is written from whatever children HAVE entries: the result is a lower bound on the true max,
 * and since `mergeLayerWord` is a floor over the procedural terrain, an under-estimate can only ever
 * fail to show something — never open a hole. Requiring all eight (the first design here) made the
 * box shrink by its own boundary each level, so a build simply stopped existing above lod 4-5.
 * MAY-LOWER is the exception and keeps the strict rule: an unknown child might be solid, so lowering
 * terrain on partial evidence could cut a hole. Cells folded from an incomplete group are marked
 * `LAYER_PARTIAL` and can never carry the flag.
 *
 * LIGHT IS MAX-FOLDED per field, sky and block independently of the weight winner. A coarse cell
 * containing any lit surface should read lit — the alternative is black blobs where a build is — and
 * the values being folded are the real lod-0 relight (full neighbour exchange, real geometry), which
 * is strictly better information than a coarse standalone pass can produce. The owner applies them
 * as a FLOOR after its own relight, so the coarse pass still shadows and radiates while the folded
 * detail wins wherever it is brighter (see `raiseLayerLight`).
 *
 * THE EMISSIVE MATERIAL OUTRANKS THE STRONGEST ONE, not just ties it. A torch is a wood column with
 * a lava tip: fold them together by weight alone and the column wins the cell, the emitter is gone,
 * and the coarse relight (which seeds block light from solid emissive MATERIALS) has nothing to emit
 * from — a lamp that lights nothing at range. Weight still comes from the strongest child, so
 * nothing thins; only the material follows the light.
 *
 * MAY-LOWER (`LAYER_MAY_LOWER`) is the one thing this has that fold.ts does not, and it exists
 * because the fine and coarse weight conventions disagree by design. A fine weight saturates within
 * ONE FINE VOXEL of the surface; a coarse weight is measured in COARSE voxels (`coarseWeight`) to
 * keep the ±2-voxel crossing band the mesher interpolates at every level. So a 2x2x2 group of fine
 * air sitting just above the ground folds to -0.5 while the procedural coarse voxel there reads
 * slightly SOLID — and a fold that simply overwrote would cut a one-coarse-voxel notch along the
 * ground inside every edit box, i.e. a moat around every player build (0.5 m at lod 1, 2 m at lod 3).
 *
 * So a folded voxel may only LOWER the procedural terrain where it is the interior of a genuine
 * hole: air whose six neighbours are also air (`erodeMayLower`). Everywhere else the merge is a max
 * (`mergeLayerWord`), which can never erode terrain or open a hole. The cost is the honest one: a
 * carve has to be about two coarse voxels across to appear at a level, so a doorway fades out by
 * lod 3 while a quarry reads at every level — the mirror of the additive side, where a sub-voxel
 * build is fattened to a full coarse voxel rather than lost.
 */
import { CHUNK_SIZE, MATERIAL_MASK, WEIGHT_MAX_PACKED, MATERIAL_SHIFT, SURFACE_PACKED_THRESHOLD, BLOCK_LIGHT_MASK, BLOCK_LIGHT_SHIFT, LIGHT_MASK, WEIGHT_MASK, WEIGHT_SHIFT, } from './constants.js';
import { MATERIAL_EMISSION_LUT } from '../materials/Materials.js';
import { voxelIndex } from './voxelData.js';
/**
 * Bit 21 of a LAYER word (never of a chunk word): this voxel is the interior of a carve, so it may
 * lower the procedural terrain it is merged into. The low 16 bits stay a normal packed voxel and
 * bits 16-20 stay the block-light field, so a layer word is a chunk word plus this flag.
 */
export const LAYER_MAY_LOWER = 1 << 21;
/**
 * Bit 22 of a LAYER word: this cell was folded from an INCOMPLETE group (some child had no entry).
 * Its value is a lower bound, which is safe for a floor merge but never enough to lower terrain.
 */
export const LAYER_PARTIAL = 1 << 22;
/** The value bits of a layer word: weight + material + sky light + block light (0-20). */
const LAYER_VALUE_MASK = (1 << 21) - 1;
/** Clamp a box to the chunk and grow it by `pad` voxels on every side. */
export function padBox(box, pad) {
    const lo = (v) => Math.max(0, v - pad);
    const hi = (v) => Math.min(CHUNK_SIZE - 1, v + pad);
    return { x0: lo(box.x0), y0: lo(box.y0), z0: lo(box.z0), x1: hi(box.x1), y1: hi(box.y1), z1: hi(box.z1) };
}
/** Union of two boxes (both already clamped to the chunk). */
export function unionBox(a, b) {
    return {
        x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0), z0: Math.min(a.z0, b.z0),
        x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1), z1: Math.max(a.z1, b.z1),
    };
}
/** True when the box covers no voxels. */
export function isEmptyBox(box) {
    return box.x1 < box.x0 || box.y1 < box.y0 || box.z1 < box.z0;
}
/**
 * Where a child box lands in its parent: the child's voxels halve, then shift into the child's
 * octant of the parent (`oct*` is the child chunk coordinate's low bit per axis).
 */
export function imageBox(box, octX, octY, octZ) {
    const half = CHUNK_SIZE >> 1;
    return {
        x0: (box.x0 >> 1) + octX * half, x1: (box.x1 >> 1) + octX * half,
        y0: (box.y0 >> 1) + octY * half, y1: (box.y1 >> 1) + octY * half,
        z0: (box.z0 >> 1) + octZ * half, z1: (box.z1 >> 1) + octZ * half,
    };
}
/** A layer word's value bits — weight, material and BOTH light fields, with the flags stripped. */
export function layerValue(word) {
    return word & LAYER_VALUE_MASK;
}
function packedWeightOf(word) {
    return (word >>> WEIGHT_SHIFT) & WEIGHT_MASK;
}
/** A folded voxel whose strongest child is still outside the surface (the all-carve cell). */
export function isAirWord(word) {
    return packedWeightOf(word) <= SURFACE_PACKED_THRESHOLD;
}
/**
 * Fold `box` of a child chunk into its parent's octant. Returns the parent box written, or null when
 * nothing could be written (no cell in the box had a single known child).
 *
 * `childKnown` may be null, which means the child is a real chunk array: everything in it is known,
 * and its weights are FINE-lattice weights, so this is the fold that re-measures them (see header).
 */
export function foldOctant(childWords, childKnown, box, octX, octY, octZ, sink) {
    if (isEmptyBox(box))
        return null;
    const fromChunk = childKnown === null;
    const half = CHUNK_SIZE >> 1;
    // The walk runs on the PARENT lattice and reads back down into the eight children.
    const px0 = box.x0 >> 1, px1 = box.x1 >> 1;
    const py0 = box.y0 >> 1, py1 = box.y1 >> 1;
    const pz0 = box.z0 >> 1, pz1 = box.z1 >> 1;
    let wrote = false;
    let wx0 = CHUNK_SIZE, wy0 = CHUNK_SIZE, wz0 = CHUNK_SIZE;
    let wx1 = -1, wy1 = -1, wz1 = -1;
    for (let pz = pz0; pz <= pz1; pz++) {
        for (let py = py0; py <= py1; py++) {
            for (let px = px0; px <= px1; px++) {
                let bestWord = -1;
                let bestWeight = -1;
                let bestEmissive = -1; // strongest SOLID emissive child, if the cell has one
                let bestEmissiveRank = -1;
                let known = 0;
                let solid = 0;
                let skyMax = 0;
                let blockMax = 0;
                for (let dz = 0; dz < 2; dz++) {
                    for (let dy = 0; dy < 2; dy++) {
                        for (let dx = 0; dx < 2; dx++) {
                            const ci = voxelIndex((px << 1) + dx, (py << 1) + dy, (pz << 1) + dz);
                            if (childKnown && !childKnown(ci))
                                continue;
                            known++;
                            const w = layerValue(childWords(ci));
                            const pw = packedWeightOf(w);
                            if (pw > bestWeight) {
                                bestWeight = pw;
                                bestWord = w;
                            }
                            // Light is folded PER FIELD, independent of the weight winner: a cell holding any lit
                            // surface reads lit, which is the only sane direction at range.
                            const sky = w & LIGHT_MASK;
                            if (sky > skyMax)
                                skyMax = sky;
                            const block = (w >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK;
                            if (block > blockMax)
                                blockMax = block;
                            if (pw > SURFACE_PACKED_THRESHOLD) {
                                solid++;
                                const emission = MATERIAL_EMISSION_LUT[(w >>> MATERIAL_SHIFT) & MATERIAL_MASK];
                                if (emission > 0 && (emission > bestEmissiveRank
                                    || (emission === bestEmissiveRank && pw > packedWeightOf(bestEmissive)))) {
                                    bestEmissive = w;
                                    bestEmissiveRank = emission;
                                }
                            }
                        }
                    }
                }
                if (known === 0 || bestWord < 0)
                    continue;
                let out = bestWord;
                if (bestWeight > SURFACE_PACKED_THRESHOLD) {
                    // The cell's material follows its light if it has an emitter (the coarse relight seeds
                    // block light from solid emissive MATERIALS, so losing it loses the lamp).
                    if (bestEmissive >= 0)
                        out = bestEmissive;
                    // Full cells hold their weight forever; partial ones fade a step per level. See header.
                    const full = known === 8 && solid === 8;
                    let pw = fromChunk ? WEIGHT_MAX_PACKED : bestWeight;
                    if (!full)
                        pw = Math.max(SURFACE_PACKED_THRESHOLD + 1, pw - 1);
                    out = (out & ~(WEIGHT_MASK << WEIGHT_SHIFT)) | (pw << WEIGHT_SHIFT);
                }
                out = (out & ~LIGHT_MASK & ~(BLOCK_LIGHT_MASK << BLOCK_LIGHT_SHIFT))
                    | skyMax | (blockMax << BLOCK_LIGHT_SHIFT);
                if (known < 8)
                    out |= LAYER_PARTIAL;
                const pi = voxelIndex(px + octX * half, py + octY * half, pz + octZ * half);
                sink.set(pi, out);
                wrote = true;
                if (px < wx0)
                    wx0 = px;
                if (px > wx1)
                    wx1 = px;
                if (py < wy0)
                    wy0 = py;
                if (py > wy1)
                    wy1 = py;
                if (pz < wz0)
                    wz0 = pz;
                if (pz > wz1)
                    wz1 = pz;
            }
        }
    }
    if (!wrote)
        return null;
    return imageBox({ x0: wx0 << 1, y0: wy0 << 1, z0: wz0 << 1, x1: wx1 << 1, y1: wy1 << 1, z1: wz1 << 1 }, octX, octY, octZ);
}
/**
 * Set `LAYER_MAY_LOWER` on the voxels of `box` that are the interior of a carve: air whose six
 * neighbours are known air. `neighbourWord` resolves a voxel that may sit in an adjacent chunk (it
 * returns -1 for unknown, which counts as NOT air — the conservative direction: a missing neighbour
 * withholds the flag, so terrain can only ever be over-preserved, never eroded).
 *
 * A `LAYER_PARTIAL` cell can never earn the flag, its own or a neighbour's: its value is a lower
 * bound over the children that happened to have entries, and lowering terrain on that evidence is
 * how you cut a hole in a hillside.
 */
export function erodeMayLower(box, own, neighbourWord) {
    if (isEmptyBox(box))
        return;
    const airAt = (lx, ly, lz) => {
        const w = neighbourWord(lx, ly, lz);
        return w >= 0 && !(w & LAYER_PARTIAL) && isAirWord(w);
    };
    for (let z = box.z0; z <= box.z1; z++) {
        for (let y = box.y0; y <= box.y1; y++) {
            for (let x = box.x0; x <= box.x1; x++) {
                const i = voxelIndex(x, y, z);
                const w = own.get(i);
                if (w < 0)
                    continue;
                const may = !(w & LAYER_PARTIAL) && isAirWord(w)
                    && airAt(x - 1, y, z) && airAt(x + 1, y, z)
                    && airAt(x, y - 1, z) && airAt(x, y + 1, z)
                    && airAt(x, y, z - 1) && airAt(x, y, z + 1);
                const kept = w & ~LAYER_MAY_LOWER;
                own.set(i, may ? (kept | LAYER_MAY_LOWER) : kept);
            }
        }
    }
}
/**
 * Merge one layer word into the procedurally generated voxel under it.
 *
 * A plain max everywhere except inside a carve: the layer is a FLOOR on the terrain, so a build
 * always shows and terrain can never be eroded by the fine/coarse weight-convention gap. Only a
 * may-lower voxel overrides outright, which is how a carve becomes visible at range. A winning layer
 * word brings its folded LIGHT with it (weight, material and both light fields are one value).
 */
export function mergeLayerWord(current, layerWord) {
    const value = layerValue(layerWord);
    if (layerWord & LAYER_MAY_LOWER)
        return value;
    return packedWeightOf(value) > packedWeightOf(current) ? value : current;
}
/**
 * Raise a voxel's light to the folded values, per field, without touching its geometry.
 *
 * The second half of the light model: the owner relights a coarse chunk the way it always did (so a
 * new wall still shadows and a lamp still radiates at coarse resolution), then floors the result with
 * the light folded up from lod 0 — which is the real relight, with neighbour exchange and true
 * geometry, and is therefore the better number wherever it is brighter. Max-only, so it can only
 * ever fail to brighten; it can never black out a lit surface.
 */
export function raiseLayerLight(current, layerWord) {
    const sky = Math.max(current & LIGHT_MASK, layerWord & LIGHT_MASK);
    const block = Math.max((current >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK, (layerWord >>> BLOCK_LIGHT_SHIFT) & BLOCK_LIGHT_MASK);
    return (current & ~LIGHT_MASK & ~(BLOCK_LIGHT_MASK << BLOCK_LIGHT_SHIFT))
        | sky | (block << BLOCK_LIGHT_SHIFT);
}
//# sourceMappingURL=foldDense.js.map